import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { vehicleAPI } from '../../utils/api';
import { adminAPI } from '../../utils/adminAPI';
import { Plus, Trash2, Car } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AdminVehicles = () => {
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add Make Dialog
  const [addMakeDialogOpen, setAddMakeDialogOpen] = useState(false);
  const [newMakeName, setNewMakeName] = useState('');
  const [newMakeLogo, setNewMakeLogo] = useState('');
  
  // Add Model Dialog
  const [addModelDialogOpen, setAddModelDialogOpen] = useState(false);
  const [selectedMakeForModel, setSelectedMakeForModel] = useState(null);
  const [newModelName, setNewModelName] = useState('');
  
  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [makeToDelete, setMakeToDelete] = useState(null);

  useEffect(() => {
    fetchMakes();
  }, []);

  const fetchMakes = async () => {
    setLoading(true);
    try {
      const response = await vehicleAPI.getMakes();
      
      // Fetch models for each make
      const makesWithModels = await Promise.all(
        response.data.makes.map(async (make) => {
          try {
            const modelsRes = await vehicleAPI.getModels(make.id);
            return { ...make, models: modelsRes.data.models };
          } catch (error) {
            return { ...make, models: [] };
          }
        })
      );
      
      setMakes(makesWithModels);
    } catch (error) {
      console.error('Error fetching makes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load vehicle data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMake = async () => {
    if (!newMakeName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a make name',
        variant: 'destructive',
      });
      return;
    }

    try {
      await adminAPI.createVehicleMake({
        name: newMakeName,
        logo: newMakeLogo || 'https://via.placeholder.com/100'
      });
      
      toast({
        title: 'Success',
        description: 'Vehicle make added successfully',
      });
      
      setAddMakeDialogOpen(false);
      setNewMakeName('');
      setNewMakeLogo('');
      fetchMakes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add vehicle make',
        variant: 'destructive',
      });
    }
  };

  const handleAddModel = async () => {
    if (!newModelName.trim() || !selectedMakeForModel) {
      toast({
        title: 'Error',
        description: 'Please enter a model name',
        variant: 'destructive',
      });
      return;
    }

    try {
      await adminAPI.createVehicleModel({
        make_id: selectedMakeForModel.id,
        make_name: selectedMakeForModel.name,
        model: newModelName
      });
      
      toast({
        title: 'Success',
        description: 'Model added successfully',
      });
      
      setAddModelDialogOpen(false);
      setNewModelName('');
      setSelectedMakeForModel(null);
      fetchMakes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add model',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (make) => {
    setMakeToDelete(make);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!makeToDelete) return;

    try {
      await adminAPI.deleteVehicleMake(makeToDelete.id);
      toast({
        title: 'Success',
        description: 'Vehicle make and its models deleted',
      });
      fetchMakes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete vehicle make',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setMakeToDelete(null);
    }
  };

  const openAddModelDialog = (make) => {
    setSelectedMakeForModel(make);
    setAddModelDialogOpen(true);
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vehicle Management</h1>
          <p className="text-gray-600">Manage car makes and models</p>
        </div>
        <Button 
          onClick={() => setAddMakeDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Vehicle Make
        </Button>
      </div>

      {loading ? (
        <p>Loading vehicles...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {makes.map((make) => (
            <Card key={make.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <Car className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{make.name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {make.models?.length || 0} models
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(make)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {make.models && make.models.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {make.models.slice(0, 6).map((model, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {model}
                        </span>
                      ))}
                      {make.models.length > 6 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          +{make.models.length - 6} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No models added yet</p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAddModelDialog(make)}
                  className="w-full"
                >
                  <Plus className="mr-2 h-3 w-3" />
                  Add Model
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Make Dialog */}
      <Dialog open={addMakeDialogOpen} onOpenChange={setAddMakeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Vehicle Make</DialogTitle>
            <DialogDescription>
              Add a new vehicle manufacturer to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="make-name">Make Name *</Label>
              <Input
                id="make-name"
                placeholder="e.g., Toyota, BMW, Mercedes-Benz"
                value={newMakeName}
                onChange={(e) => setNewMakeName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="make-logo">Logo URL (optional)</Label>
              <Input
                id="make-logo"
                placeholder="https://example.com/logo.png"
                value={newMakeLogo}
                onChange={(e) => setNewMakeLogo(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMakeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMake} className="bg-blue-600 hover:bg-blue-700">
              Add Make
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Model Dialog */}
      <Dialog open={addModelDialogOpen} onOpenChange={setAddModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Model to {selectedMakeForModel?.name}</DialogTitle>
            <DialogDescription>
              Add a new model for this vehicle make
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="model-name">Model Name *</Label>
            <Input
              id="model-name"
              placeholder="e.g., Corolla, 3 Series, C-Class"
              value={newModelName}
              onChange={(e) => setNewModelName(e.target.value)}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModelDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddModel} className="bg-blue-600 hover:bg-blue-700">
              Add Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle Make</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {makeToDelete?.name}? This will also delete all 
              models associated with this make. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminVehicles;
